import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MONTH_LAST_BANK_DAY, eachDay, monthBounds } from "../../utils/financeCalendar";
import { expandTransactionParts } from "../../utils/financeSplits";
import { partMatchesCategoryFilter } from "../../utils/financeCategoryFilter";

function parseIso(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function isoToday() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function countsTowardRemaining(tx, household, selectedIds) {
  if (!partMatchesCategoryFilter(tx, selectedIds)) return false;
  if (!household && tx.exempt_from_budget) return false;
  if (household && tx.is_internal_transfer) return false;
  return Number(tx.amount || 0) < 0;
}

export default function FinanceBudgetRemainingChart({
  transactions = [],
  dateFrom,
  dateTo,
  household = false,
  budgetMonthly = null,
  monthMode = MONTH_LAST_BANK_DAY,
  formatMoney,
  emptyHint = "Sæt et månedsbudget under Budget for at se resterende saldo.",
  selectedIds = [],
}) {
  const theme = useTheme();
  const todayIso = isoToday();
  const monthly = budgetMonthly == null ? null : Number(budgetMonthly);

  const data = useMemo(() => {
    if (monthly == null || Number.isNaN(monthly)) return [];
    const days = eachDay(dateFrom, dateTo);
    const netByDay = new Map();
    (transactions || []).flatMap(expandTransactionParts).forEach((tx) => {
      if (!countsTowardRemaining(tx, household, selectedIds)) return;
      const day = String(tx.booking_date).slice(0, 10);
      netByDay.set(day, (netByDay.get(day) || 0) + Number(tx.amount || 0));
    });

    const netInMonth = new Map();
    return days.map((day) => {
      const bounds = monthBounds(day, monthMode);
      const net = netByDay.get(day) || 0;
      netInMonth.set(
        bounds.from,
        (netInMonth.get(bounds.from) || 0) + (day <= todayIso ? net : 0)
      );
      const remaining = Math.round((monthly + (netInMonth.get(bounds.from) || 0)) * 100) / 100;
      const inRange = day <= todayIso;
      return {
        date: day,
        remaining: inRange ? remaining : null,
        above: inRange && remaining >= 0 ? remaining : inRange ? 0 : null,
        below: inRange && remaining < 0 ? remaining : inRange ? 0 : null,
      };
    });
  }, [transactions, dateFrom, dateTo, household, monthly, monthMode, todayIso, selectedIds]);

  const remainingToday = useMemo(() => {
    const row =
      data.find((item) => item.date === todayIso) ||
      [...data].reverse().find((item) => item.remaining != null);
    return row?.remaining ?? null;
  }, [data, todayIso]);
  const showToday =
    todayIso >= String(dateFrom).slice(0, 10) && todayIso <= String(dateTo).slice(0, 10);

  if (monthly == null || Number.isNaN(monthly)) {
    return (
      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Budget resterende
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyHint}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Budget resterende
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Månedsbudget minus udgifter. Kan gå under 0. Nulstilles ved hver økonomimåned.
      </Typography>
      <Box sx={{ position: "relative", width: "100%", height: 280 }}>
        {showToday && remainingToday != null ? (
          <Box sx={{ position: "absolute", top: 4, right: 16, zIndex: 1, textAlign: "right", pointerEvents: "none" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              I dag
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: remainingToday < 0 ? "error.main" : "success.dark",
                lineHeight: 1.2,
              }}
            >
              {typeof formatMoney === "function" ? formatMoney(remainingToday) : remainingToday}
            </Typography>
          </Box>
        ) : null}
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickFormatter={(value) =>
                parseIso(value).toLocaleDateString("da-DK", { day: "numeric", month: "short" })
              }
              minTickGap={24}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
              tickFormatter={(value) =>
                Number(value || 0).toLocaleString("da-DK", { maximumFractionDigits: 0 })
              }
              width={72}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const remaining = payload[0]?.payload?.remaining;
                if (remaining == null) return null;
                const over = remaining < 0;
                return (
                  <Box
                    sx={{
                      px: 1.25,
                      py: 1,
                      bgcolor: "background.paper",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      {parseIso(label).toLocaleDateString("da-DK")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: over ? "error.main" : "success.dark" }}
                    >
                      {typeof formatMoney === "function" ? formatMoney(remaining) : remaining}
                    </Typography>
                  </Box>
                );
              }}
            />
            <ReferenceLine
              y={0}
              stroke={theme.palette.text.primary}
              strokeDasharray="4 4"
              strokeWidth={1.25}
            />
            {showToday ? (
              <ReferenceLine
                x={todayIso}
                stroke={theme.palette.text.secondary}
                strokeDasharray="3 3"
              />
            ) : null}
            <Area
              type="stepAfter"
              dataKey="above"
              name="Resterende"
              stroke={theme.palette.success.dark}
              fill={theme.palette.success.main}
              fillOpacity={0.22}
              strokeWidth={1.75}
              baseValue={0}
              connectNulls={false}
              dot={false}
              activeDot={{ r: 3 }}
            />
            <Area
              type="stepAfter"
              dataKey="below"
              name="Over budget"
              stroke={theme.palette.error.main}
              fill={theme.palette.error.main}
              fillOpacity={0.28}
              strokeWidth={1.75}
              baseValue={0}
              connectNulls={false}
              dot={false}
              activeDot={{ r: 3 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
